import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import jsQR from "jsqr";
import { useHidScanner } from "../contexts/HidScannerContext";

/**
 * iCaard Universal Scanner — one professional scanner for the whole platform.
 *  • Camera scanning (QR + barcodes via BarcodeDetector when supported)
 *  • Handheld / USB / Bluetooth hardware scanners (keyboard-wedge capture)
 *  • Torch (flashlight) + camera flip, duplicate-scan guard, beep + vibrate
 * Emits via BOTH onScan and onResult so all existing callers keep working.
 */
export default function QrScanner({ onScan, onResult, onError, onStatus, compact = false, fill = false }) {
  const ref = useRef(null);
  const containerId = useRef(`qr-reader-${Math.random().toString(36).slice(2, 9)}`);
  const stoppedRef = useRef(false);
  const lastScanRef = useRef({ text: "", at: 0 });
  const [phase, setPhase] = useState("starting"); // starting | ready | denied | nocamera
  const [flash, setFlash] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [hwHit, setHwHit] = useState(false);

  const setStatus = (p, detail = "") => {
    setPhase(p);
    try { onStatus?.({ phase: p, detail }); } catch {}
  };

  const successFeedback = () => {
    try { navigator.vibrate?.(120); } catch {}
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const g = ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.frequency.value = 1200; g.gain.value = 0.15;
      osc.start(); osc.stop(ctx.currentTime + 0.12);
      setTimeout(() => ctx.close().catch(() => {}), 400);
    } catch {}
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  };

  const emit = (text, viaHardware = false) => {
    if (stoppedRef.current) return;
    const now = Date.now();
    // Duplicate guard — same code within 2.5s is one scan, not two.
    if (lastScanRef.current.text === text && now - lastScanRef.current.at < 2500) return;
    lastScanRef.current = { text, at: now };
    successFeedback();
    if (viaHardware) { setHwHit(true); setTimeout(() => setHwHit(false), 1500); }
    try { onScan?.(text); } catch (e) { console.error("[Scanner] onScan threw", e); }
    try { onResult?.(text); } catch (e) { console.error("[Scanner] onResult threw", e); }
  };

  // ── Hardware scanner (keyboard-wedge) — USB / Bluetooth / laser guns ──────
  // While this scanner is open it becomes the newest HID subscriber, so
  // handheld scans land HERE instead of the global check-in router.
  useHidScanner((code) => emit(code, true));

  // ── Camera ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const scanner = new Html5Qrcode(containerId.current, { verbose: false });
    ref.current = scanner;
    stoppedRef.current = false;
    setTorchOn(false);
    setTorchAvailable(false);

    const config = {
      fps: 25,
      qrbox: (vw, vh) => {
        const m = Math.min(vw, vh);
        return { width: Math.floor(m * 0.85), height: Math.floor(m * 0.85) };
      },
      aspectRatio: 1.0,
      disableFlip: false,
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      videoConstraints: {
        facingMode: { ideal: facing },
        width: { ideal: 1280 }, height: { ideal: 720 },
        advanced: [{ focusMode: "continuous" }],
      },
    };

    const probeTorch = () => {
      try {
        const caps = ref.current?.getRunningTrackCapabilities?.();
        if (caps && "torch" in caps) setTorchAvailable(true);
      } catch {}
    };

    const start = async () => {
      if (!navigator?.mediaDevices?.getUserMedia) {
        setStatus("denied", "Browser doesn't support camera APIs");
        onError?.("Camera APIs are not available in this browser.");
        return;
      }
      try {
        await scanner.start({ facingMode: { exact: facing } }, config, (t) => emit(t), () => {});
        setStatus("ready", facing); probeTorch(); return;
      } catch {}
      try {
        await scanner.start({ facingMode: facing }, config, (t) => emit(t), () => {});
        setStatus("ready", facing); probeTorch(); return;
      } catch (e) { console.warn("[Scanner] facing camera failed, falling back", e); }
      try {
        const devices = await Html5Qrcode.getCameras();
        if (!devices?.length) {
          setStatus("nocamera", "No camera detected");
          onError?.("No camera detected — you can still use a handheld scanner or type the code.");
          return;
        }
        const pick = facing === "environment"
          ? (devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1])
          : (devices.find((d) => /front|user|face/i.test(d.label)) || devices[0]);
        await scanner.start(pick.id, config, (t) => emit(t), () => {});
        setStatus("ready", pick.label || pick.id); probeTorch();
      } catch (e2) {
        const msg = e2?.message || e2?.toString() || "Camera access denied";
        const denied = /denied|not allow|permission/i.test(msg);
        setStatus(denied ? "denied" : "nocamera", msg);
        onError?.(denied
          ? "Camera permission was denied. Allow camera access — or use a handheld scanner."
          : `Camera failed to start: ${msg}`);
      }
    };
    start();

    return () => {
      stoppedRef.current = true;
      try {
        if (scanner && scanner.isScanning) {
          scanner.stop().catch(() => {}).finally(() => { try { scanner.clear(); } catch {} });
        } else {
          try { scanner.clear(); } catch {}
        }
      } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facing]);

  const toggleTorch = async () => {
    try {
      await ref.current?.applyVideoConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch { setTorchAvailable(false); }
  };

  // ── Scan a QR from a saved image / screenshot ─────────────────────────────
  const fileRef = useRef(null);
  const scanImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const obj = URL.createObjectURL(f);
    try {
      const img = new Image();
      img.src = obj;
      await img.decode();
      for (const maxW of [1000, 1600, img.naturalWidth]) {
        const scale = Math.min(1, maxW / img.naturalWidth);
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const d = ctx.getImageData(0, 0, w, h);
        const res = jsQR(d.data, w, h, { inversionAttempts: "attemptBoth" });
        if (res?.data) { emit(res.data); return; }
      }
      onError?.("No QR code found in that image — try a clearer, closer photo.");
    } catch (err) {
      console.warn("[Scanner] image scan failed", err);
      onError?.("Could not read that image — try a different photo.");
    } finally { URL.revokeObjectURL(obj); }
  };

  const corner = "absolute w-7 h-7 border-[#55C99B] pointer-events-none";
  const previewHeight = fill ? "100%" : compact ? "clamp(170px, 32dvh, 280px)" : 240;
  return (
    <div className={`relative select-none ${fill ? "flex-1 min-h-0 flex flex-col" : ""}`} data-testid="qr-scanner">
      <div className={`relative ${fill ? "flex-1 min-h-0" : ""}`} data-testid="qr-preview">
        <div id={containerId.current} className={`rounded-2xl overflow-hidden bg-[#061F1B] border border-[#2C6254] ${fill ? "h-full min-h-0" : ""}`} style={{ height: previewHeight, minHeight: fill ? 160 : compact ? 160 : 240, maxHeight: compact && !fill ? "38dvh" : undefined }} />

      {/* Professional frame — corner brackets + laser line */}
      <div className="absolute inset-0 pointer-events-none rounded-2xl border border-white/10" />
      {phase === "ready" && (
        <>
          <div className={`${corner} border-t-[3px] border-l-[3px] rounded-tl-lg`} style={{ top: 14, left: 14 }} />
          <div className={`${corner} border-t-[3px] border-r-[3px] rounded-tr-lg`} style={{ top: 14, right: 14 }} />
          <div className={`${corner} border-b-[3px] border-l-[3px] rounded-bl-lg`} style={{ bottom: 14, left: 14 }} />
          <div className={`${corner} border-b-[3px] border-r-[3px] rounded-br-lg`} style={{ bottom: 14, right: 14 }} />
          {!flash && (
            <div className="absolute left-6 right-6 pointer-events-none" style={{ top: 0, height: "100%", overflow: "hidden" }}>
              <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: "linear-gradient(90deg, transparent, #55C99B, transparent)", boxShadow: "0 0 12px #55C99B", animation: "qr-scanline 2.2s ease-in-out infinite" }} />
            </div>
          )}
          {/* Controls — torch + camera flip */}
          <div className="absolute top-2 right-2 flex gap-1.5">
            {torchAvailable && (
              <button onClick={toggleTorch} data-testid="scanner-torch-btn" aria-label="Toggle flashlight"
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-base backdrop-blur border ${torchOn ? "bg-[#F5B400] border-[#F5B400] text-black" : "bg-black/50 border-white/20 text-white"}`}>🔦</button>
            )}
            <button onClick={() => setFacing(facing === "environment" ? "user" : "environment")}
                    data-testid="scanner-flip-btn" aria-label="Switch camera"
                    className="w-9 h-9 rounded-full flex items-center justify-center text-base bg-black/50 border border-white/20 text-white backdrop-blur">🔄</button>
          </div>
        </>
      )}

      {flash && (
        <div className="absolute inset-0 pointer-events-none rounded-2xl border-4 border-[#10B981] bg-[#10B981]/15 flex items-center justify-center" data-testid="qr-scan-success-flash">
          <span className="text-[#10B981] font-black text-lg">✓ Scanned</span>
        </div>
      )}
      <style>{`@keyframes qr-scanline { 0%,100% { top: 8%; } 50% { top: 88%; } }`}</style>

      {phase !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl text-center px-4" data-testid="qr-scanner-phase">
          <div className="text-xs sm:text-sm text-white max-w-xs">
            {phase === "starting" && "Starting camera…"}
            {phase === "denied" && "🚫 Camera permission denied. Allow camera access — or plug in a handheld scanner and just scan."}
            {phase === "nocamera" && "📵 No camera detected — connect a handheld scanner and just scan, it works here too."}
          </div>
        </div>
      )}
      </div>

      {/* Hardware-scanner support chip + image scan */}
      <div className={`mt-1.5 flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-lg py-1 transition-colors ${hwHit ? "text-[#8BE0B7]" : "text-[#BED8CC]"}`} data-testid="scanner-hw-chip">
        {hwHit ? "✓ Handheld scanner read received" : "🔌 USB / Bluetooth handheld scanners supported — just point & scan"}
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={scanImage} data-testid="scanner-image-input" />
      <button onClick={() => fileRef.current?.click()} data-testid="scanner-image-btn"
              className="mt-1 w-full py-2.5 rounded-xl bg-[#124A3B] border border-[#2C6254] text-xs font-bold text-white hover:bg-[#185D49] transition-colors">
        🖼 Scan a QR image from your gallery
      </button>
    </div>
  );
}
